import { useTranslation } from 'react-i18next';
import type { TreatmentPlanTemplateResponse } from '../../api/generated';
import type { SimulationTemplateRowState } from '../../hooks/useYearPlanSimulationForm';
import { selectionEconomics } from '../../services/yearPlanSimulationService';
import { formatDkk } from '../../utils/currencyUtils';
import { Checkbox, InputFieldNumber } from '../UI';
import styles from './YearPlanSimulationModal.module.css';

interface SimulationTemplateRowProps {
  template: TreatmentPlanTemplateResponse;
  row: SimulationTemplateRowState;
  onChange: (patch: Partial<SimulationTemplateRowState>) => void;
}

const toCount = (value: string): number => (value ? Number(value) : 0);

export const SimulationTemplateRow = ({ template, row, onChange }: SimulationTemplateRowProps) => {
  const { t, i18n } = useTranslation();
  const economics = selectionEconomics(template, row.courseCount, row.capacity);
  const hasCalculation = row.isSelected && economics.patientCount > 0;
  const hasTariff = economics.revenuePerPatient > 0;

  return (
    <div className={styles.templateRow}>
      <div className={styles.templateName}>
        <Checkbox
          id={`simulation-template-${template.id}`}
          label={template.name}
          isChecked={row.isSelected}
          onChange={(isSelected) => onChange({ isSelected })}
        />
      </div>
      <InputFieldNumber
        label={t('simulation-capacity-label')}
        value={row.capacity}
        disable={!row.isSelected}
        error={row.isSelected && row.capacity === 0}
        onChange={(e) => onChange({ capacity: toCount(e.target.value) })}
      />
      <InputFieldNumber
        label={t('simulation-courses-label')}
        value={row.courseCount}
        disable={!row.isSelected}
        error={row.isSelected && row.courseCount === 0}
        onChange={(e) => onChange({ courseCount: toCount(e.target.value) })}
      />
      {hasCalculation && (
        <p className={styles.calculation}>
          {t(hasTariff ? 'simulation-calculation' : 'simulation-calculation-no-tariff', {
            count: row.courseCount,
            capacity: row.capacity,
            patients: economics.patientCount,
            perPatient: formatDkk(economics.revenuePerPatient, i18n.language),
            revenue: formatDkk(economics.revenue, i18n.language)
          })}
        </p>
      )}
    </div>
  );
};
